import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import {
  Buildings, Coins, ListChecks, ShieldCheck, ArrowLeft, ArrowRight, Check, Plus, Trash,
} from "@phosphor-icons/react";
import { api, formatApiError } from "../../lib/api";
import { computePayment, PRESET_DOCS } from "../../lib/calc";
import { money } from "../../lib/format";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Button } from "../../components/ui/button";
import { Switch } from "../../components/ui/switch";
import { Checkbox } from "../../components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import DateTimePicker from "../../components/DateTimePicker";

const STEPS = [
  { icon: Buildings, label: "Business" },
  { icon: Coins, label: "Terms" },
  { icon: ListChecks, label: "Documents" },
  { icon: ShieldCheck, label: "Security" },
];

const emptyBusiness = {
  legal_name: "", dba: "", entity_type: "LLC", ein: "", address: "", industry: "",
  years_in_business: "", owner_name: "", owner_email: "", owner_phone: "", ownership_pct: "",
};
const emptyTerms = {
  credit_limit: "", draw_period_months: "12", repayment_term_value: "12", repayment_term_unit: "months",
  pricing_mode: "apr", apr: "", factor_rate: "", payment_frequency: "weekly",
  origination_fee_type: "pct", origination_fee_value: "", draw_fee_pct: "", monthly_maintenance_fee: "",
  late_fee: "", wire_ach_fee: "", personal_guarantee: true, ucc_filing: false, show_tcc: true,
  prepayment_terms: "", offer_expiration: "", funding_timeline_note: "Funds available within 24 hours of final approval.",
  internal_notes: "",
};

function Field({ label, children, hint }) {
  return (
    <div className="space-y-1.5">
      <Label className="overline text-muted-foreground">{label}</Label>
      {children}
      {hint && <p className="text-[11px] text-muted-foreground">{hint}</p>}
    </div>
  );
}

export default function DealBuilder() {
  const { id } = useParams();
  const editing = !!id;
  const nav = useNavigate();
  const [step, setStep] = useState(0);
  const [business, setBusiness] = useState(emptyBusiness);
  const [terms, setTerms] = useState(emptyTerms);
  const [docs, setDocs] = useState([]);
  const [pin, setPin] = useState("");
  const [linkMode, setLinkMode] = useState("offer");
  const [linkCustom, setLinkCustom] = useState("");
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [loaded, setLoaded] = useState(!editing);

  useEffect(() => {
    if (!editing) return;
    api.get(`/deals/${id}`).then((r) => {
      const d = r.data;
      setBusiness({ ...emptyBusiness, ...d.business });
      setTerms({ ...emptyTerms, ...d.terms });
      setDocs(d.documents.map((x) => ({ id: x.id, name: x.name, description: x.description, instruction: x.instruction, required: x.required })));
      setLinkMode(d.security?.link_expiration_mode || "offer");
    }).catch(() => toast.error("Could not load deal")).finally(() => setLoaded(true));
  }, [id, editing]);

  const bset = (k, v) => { setBusiness((p) => ({ ...p, [k]: v })); if (errors[k]) setErrors((e) => ({ ...e, [k]: false })); };
  const tset = (k, v) => { setTerms((p) => ({ ...p, [k]: v })); if (errors[k]) setErrors((e) => ({ ...e, [k]: false })); };

  const preview = useMemo(() => computePayment(terms), [terms]);

  const togglePreset = (name) => {
    setDocs((p) => {
      const exists = p.find((d) => d.name === name && !d._custom);
      if (exists) return p.filter((d) => d !== exists);
      return [...p, { name, description: "", instruction: "", required: true }];
    });
  };
  const addCustom = () =>
    setDocs((p) => [...p, { name: "", description: "", instruction: "", required: true, _custom: true }]);
  const updateDoc = (idx, k, v) => setDocs((p) => p.map((d, i) => (i === idx ? { ...d, [k]: v } : d)));
  const removeDoc = (idx) => setDocs((p) => p.filter((_, i) => i !== idx));

  const validate = () => {
    const e = {};
    if (step === 0) {
      if (!business.legal_name) e.legal_name = true;
      if (!business.owner_name) e.owner_name = true;
      if (!business.owner_email) e.owner_email = true;
      else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(business.owner_email)) e.owner_email = "Enter a valid email";
    }
    if (step === 1) {
      if (!terms.credit_limit) e.credit_limit = true;
      if (!terms.offer_expiration) e.offer_expiration = true;
      if (terms.pricing_mode === "apr" ? !terms.apr : !terms.factor_rate)
        e[terms.pricing_mode === "apr" ? "apr" : "factor_rate"] = true;
    }
    if (step === 3 && !editing) {
      if (!(pin.length >= 4 && pin.length <= 8 && /^\d+$/.test(pin))) e.pin = true;
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const proceed = () => {
    if (validate()) { step < 3 ? setStep((s) => s + 1) : save(); }
    else toast.error("Please complete the highlighted fields");
  };
  const errCls = (k) => (errors[k] ? " border-destructive ring-1 ring-destructive/30 focus-visible:ring-destructive/40" : "");

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        business, terms,
        documents: docs.filter((d) => d.name).map((d) => ({ id: d.id, name: d.name, description: d.description, instruction: d.instruction, required: d.required })),
        pin: pin || undefined,
        link_expiration_mode: linkMode,
        link_custom_expiration: linkMode === "custom" ? linkCustom : null,
      };
      let dealId = id;
      if (editing) {
        await api.put(`/deals/${id}`, payload);
        if (pin) await api.post(`/deals/${id}/set-pin`, { pin });
        toast.success("Deal updated");
      } else {
        const { data } = await api.post("/deals", payload);
        dealId = data.id;
        toast.success("Deal created — secure link generated");
      }
      nav(`/admin/deals/${dealId}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const inputCls = "bg-secondary border-border h-10";

  return (
    <div className="p-5 md:p-8 max-w-5xl mx-auto">
      <button onClick={() => nav(-1)} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-4" data-testid="builder-back">
        <ArrowLeft size={16} /> Back
      </button>
      <div className="overline text-primary">{editing ? "Edit Deal" : "New Deal"}</div>
      <h1 className="font-display text-3xl font-bold tracking-tight mt-1">
        {editing ? business.legal_name || "Edit Deal" : "Build a Credit Deal"}
      </h1>

      {/* Stepper */}
      <div className="flex items-center gap-2 mt-6 mb-8 overflow-x-auto">
        {STEPS.map((s, i) => (
          <button
            key={i}
            onClick={() => i < step && setStep(i)}
            data-testid={`step-${i}`}
            className={`flex items-center gap-2 rounded-xl border px-3 py-2 text-sm font-medium transition-colors shrink-0 ${
              i === step ? "border-primary/40 bg-primary/12 text-primary" :
              i < step ? "border-border bg-card text-foreground" : "border-border bg-card text-muted-foreground"
            }`}
          >
            <span className={`h-6 w-6 rounded-full flex items-center justify-center text-xs ${i < step ? "bg-primary text-primary-foreground" : "bg-secondary"}`}>
              {i < step ? <Check size={14} weight="bold" /> : i + 1}
            </span>
            {s.label}
          </button>
        ))}
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={step}
          initial={{ opacity: 0, x: 12 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -12 }}
          transition={{ duration: 0.25 }}
        >
          {step === 0 && (
            <div className="rounded-[var(--radius)] border border-border bg-card p-6 grid md:grid-cols-2 gap-4">
              <Field label="Legal Business Name"><Input data-testid="f-legal-name" className={inputCls + errCls("legal_name")} value={business.legal_name} onChange={(e) => bset("legal_name", e.target.value)} /></Field>
              <Field label="DBA (optional)"><Input className={inputCls} value={business.dba} onChange={(e) => bset("dba", e.target.value)} /></Field>
              <Field label="Entity Type">
                <Select value={business.entity_type} onValueChange={(v) => bset("entity_type", v)}>
                  <SelectTrigger className={inputCls}><SelectValue /></SelectTrigger>
                  <SelectContent>{["LLC", "Corp", "Sole Prop", "Partnership"].map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="EIN"><Input className={inputCls} value={business.ein} onChange={(e) => bset("ein", e.target.value)} placeholder="00-0000000" /></Field>
              <div className="md:col-span-2"><Field label="Business Address"><Input className={inputCls} value={business.address} onChange={(e) => bset("address", e.target.value)} /></Field></div>
              <Field label="Industry"><Input className={inputCls} value={business.industry} onChange={(e) => bset("industry", e.target.value)} /></Field>
              <Field label="Years in Business"><Input className={inputCls} value={business.years_in_business} onChange={(e) => bset("years_in_business", e.target.value)} /></Field>
              <Field label="Owner / Guarantor Name"><Input data-testid="f-owner-name" className={inputCls + errCls("owner_name")} value={business.owner_name} onChange={(e) => bset("owner_name", e.target.value)} /></Field>
              <Field label="Ownership %"><Input className={inputCls} value={business.ownership_pct} onChange={(e) => bset("ownership_pct", e.target.value)} /></Field>
              <Field label="Owner Email" hint={typeof errors.owner_email === "string" ? errors.owner_email : undefined}><Input data-testid="f-owner-email" className={inputCls + errCls("owner_email")} type="email" value={business.owner_email} onChange={(e) => bset("owner_email", e.target.value)} /></Field>
              <Field label="Owner Phone"><Input className={inputCls} value={business.owner_phone} onChange={(e) => bset("owner_phone", e.target.value)} /></Field>
            </div>
          )}

          {step === 1 && (
            <div className="grid lg:grid-cols-3 gap-5">
              <div className="lg:col-span-2 rounded-[var(--radius)] border border-border bg-card p-6 grid md:grid-cols-2 gap-4">
                <div className="md:col-span-2"><Field label="Approved Credit Limit ($)"><Input data-testid="f-credit-limit" className={inputCls + " num" + errCls("credit_limit")} type="number" value={terms.credit_limit} onChange={(e) => tset("credit_limit", e.target.value)} placeholder="250000" /></Field></div>
                <Field label="Draw Period (months)"><Input className={inputCls + " num"} type="number" value={terms.draw_period_months} onChange={(e) => tset("draw_period_months", e.target.value)} /></Field>
                <div className="grid grid-cols-2 gap-2">
                  <Field label="Repayment Term"><Input className={inputCls + " num"} type="number" value={terms.repayment_term_value} onChange={(e) => tset("repayment_term_value", e.target.value)} /></Field>
                  <Field label="Unit">
                    <Select value={terms.repayment_term_unit} onValueChange={(v) => tset("repayment_term_unit", v)}>
                      <SelectTrigger className={inputCls}><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="months">Months</SelectItem><SelectItem value="weeks">Weeks</SelectItem></SelectContent>
                    </Select>
                  </Field>
                </div>
                <Field label="Pricing Mode">
                  <Select value={terms.pricing_mode} onValueChange={(v) => tset("pricing_mode", v)}>
                    <SelectTrigger className={inputCls} data-testid="f-pricing-mode"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="apr">Interest Rate (APR %)</SelectItem><SelectItem value="factor">Factor Rate</SelectItem></SelectContent>
                  </Select>
                </Field>
                {terms.pricing_mode === "apr" ? (
                  <Field label="APR %"><Input data-testid="f-apr" className={inputCls + " num" + errCls("apr")} type="number" value={terms.apr} onChange={(e) => tset("apr", e.target.value)} placeholder="18.5" /></Field>
                ) : (
                  <Field label="Factor Rate"><Input data-testid="f-factor" className={inputCls + " num" + errCls("factor_rate")} type="number" step="0.01" value={terms.factor_rate} onChange={(e) => tset("factor_rate", e.target.value)} placeholder="1.28" /></Field>
                )}
                <Field label="Payment Frequency">
                  <Select value={terms.payment_frequency} onValueChange={(v) => tset("payment_frequency", v)}>
                    <SelectTrigger className={inputCls}><SelectValue /></SelectTrigger>
                    <SelectContent>{["daily", "weekly", "bi-weekly", "monthly"].map((x) => <SelectItem key={x} value={x} className="capitalize">{x}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
                <div className="grid grid-cols-2 gap-2">
                  <Field label="Origination Fee">
                    <Select value={terms.origination_fee_type} onValueChange={(v) => tset("origination_fee_type", v)}>
                      <SelectTrigger className={inputCls}><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="pct">% of limit</SelectItem><SelectItem value="flat">Flat $</SelectItem></SelectContent>
                    </Select>
                  </Field>
                  <Field label="Value"><Input className={inputCls + " num"} type="number" value={terms.origination_fee_value} onChange={(e) => tset("origination_fee_value", e.target.value)} /></Field>
                </div>
                <Field label="Draw Fee (%)"><Input className={inputCls + " num"} type="number" value={terms.draw_fee_pct} onChange={(e) => tset("draw_fee_pct", e.target.value)} /></Field>
                <Field label="Monthly Maintenance ($)"><Input className={inputCls + " num"} type="number" value={terms.monthly_maintenance_fee} onChange={(e) => tset("monthly_maintenance_fee", e.target.value)} /></Field>
                <Field label="Late Fee ($)"><Input className={inputCls + " num"} type="number" value={terms.late_fee} onChange={(e) => tset("late_fee", e.target.value)} /></Field>
                <Field label="Wire / ACH Fee ($)"><Input className={inputCls + " num"} type="number" value={terms.wire_ach_fee} onChange={(e) => tset("wire_ach_fee", e.target.value)} /></Field>
                <div className="md:col-span-2 flex flex-wrap gap-6 py-1">
                  <label className="flex items-center gap-2.5"><Switch checked={terms.personal_guarantee} onCheckedChange={(v) => tset("personal_guarantee", v)} data-testid="f-pg" /><span className="text-sm">Personal Guarantee</span></label>
                  <label className="flex items-center gap-2.5"><Switch checked={terms.ucc_filing} onCheckedChange={(v) => tset("ucc_filing", v)} /><span className="text-sm">UCC Filing</span></label>
                  <label className="flex items-center gap-2.5"><Switch checked={terms.show_tcc !== false} onCheckedChange={(v) => tset("show_tcc", v)} data-testid="f-show-tcc" /><span className="text-sm">Show Total Cost of Capital</span></label>
                </div>
                <div className="md:col-span-2"><Field label="Prepayment / Early Payoff Terms"><Textarea className="bg-secondary border-border" value={terms.prepayment_terms} onChange={(e) => tset("prepayment_terms", e.target.value)} /></Field></div>
                <div className="md:col-span-2"><Field label="Offer Expiration (date & time)"><DateTimePicker testId="f-expiration" value={terms.offer_expiration} invalid={errors.offer_expiration} onChange={(iso) => tset("offer_expiration", iso)} /></Field></div>
                <div className="md:col-span-2"><Field label="Funding Timeline Note"><Input className={inputCls} value={terms.funding_timeline_note} onChange={(e) => tset("funding_timeline_note", e.target.value)} /></Field></div>
                <div className="md:col-span-2"><Field label="Internal Notes (never shown to merchant)" hint="Private to your team."><Textarea className="bg-secondary border-border" value={terms.internal_notes} onChange={(e) => tset("internal_notes", e.target.value)} /></Field></div>
              </div>

              {/* Live payment preview */}
              <div className="lg:col-span-1">
                <div className="sticky top-6 rounded-[var(--radius)] border border-primary/25 bg-primary/[0.06] p-5 card-inner-glow">
                  <div className="overline text-primary">Live Payment Preview</div>
                  <div className="mt-4 space-y-3">
                    <PreviewRow label={`Payment / ${preview.payment_frequency}`} value={money(preview.estimated_payment)} big />
                    <PreviewRow label="# of payments" value={preview.number_of_payments} />
                    <PreviewRow label="Origination fee" value={money(preview.origination_fee)} />
                    <div className="h-px bg-border my-1" />
                    <PreviewRow label="Total payback" value={money(preview.total_payback)} />
                    {terms.show_tcc !== false && <PreviewRow label="Total cost of capital" value={money(preview.total_cost_of_capital)} accent />}
                  </div>
                  <p className="text-[11px] text-muted-foreground mt-4">Estimated on a full draw. Shown to the merchant.</p>
                </div>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-5">
              <div className="rounded-[var(--radius)] border border-border bg-card p-6">
                <div className="overline text-muted-foreground mb-3">Preset Library</div>
                <div className="grid sm:grid-cols-2 gap-2">
                  {PRESET_DOCS.map((name) => {
                    const on = docs.some((d) => d.name === name && !d._custom);
                    return (
                      <button
                        key={name}
                        onClick={() => togglePreset(name)}
                        data-testid={`preset-${name.slice(0, 10)}`}
                        className={`flex items-center gap-2.5 rounded-xl border px-3 py-2.5 text-left text-sm transition-colors ${on ? "border-primary/40 bg-primary/10 text-foreground" : "border-border bg-secondary/40 text-muted-foreground hover:text-foreground"}`}
                      >
                        <span className={`h-4 w-4 rounded border flex items-center justify-center ${on ? "bg-primary border-primary" : "border-border"}`}>
                          {on && <Check size={12} weight="bold" className="text-primary-foreground" />}
                        </span>
                        {name}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="rounded-[var(--radius)] border border-border bg-card p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="overline text-muted-foreground">Selected Checklist ({docs.length})</div>
                  <Button size="sm" variant="outline" onClick={addCustom} data-testid="add-custom-doc"><Plus size={16} className="mr-1" /> Custom item</Button>
                </div>
                <div className="space-y-3">
                  {docs.length === 0 && <p className="text-sm text-muted-foreground">Toggle presets above or add a custom item.</p>}
                  {docs.map((d, i) => (
                    <div key={i} className="rounded-xl border border-border bg-secondary/30 p-3">
                      <div className="flex items-start gap-3">
                        <div className="flex-1 space-y-2">
                          {d._custom ? (
                            <Input className="bg-background border-border h-9" placeholder="Document name" value={d.name} onChange={(e) => updateDoc(i, "name", e.target.value)} data-testid={`custom-name-${i}`} />
                          ) : (
                            <div className="font-medium text-sm">{d.name}</div>
                          )}
                          <Input className="bg-background border-border h-9 text-sm" placeholder="Instruction for merchant (e.g. All pages, PDF preferred)" value={d.instruction} onChange={(e) => updateDoc(i, "instruction", e.target.value)} />
                        </div>
                        <div className="flex flex-col items-end gap-2">
                          <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
                            <Switch checked={d.required} onCheckedChange={(v) => updateDoc(i, "required", v)} /> Required
                          </label>
                          <button onClick={() => removeDoc(i)} className="text-muted-foreground hover:text-destructive"><Trash size={16} /></button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="rounded-[var(--radius)] border border-border bg-card p-6 max-w-lg space-y-6">
              <div>
                <div className="overline text-muted-foreground mb-2">Access PIN {editing && "(leave blank to keep current)"}</div>
                <Input
                  data-testid="f-pin"
                  className={inputCls + " num tracking-[0.5em] text-center text-lg" + errCls("pin")}
                  inputMode="numeric"
                  maxLength={8}
                  value={pin}
                  onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 8))}
                  placeholder="4–8 digits"
                />
                <p className="text-[11px] text-muted-foreground mt-1.5">The merchant enters this code to unlock their offer. Deliver it separately.</p>
              </div>
              <div>
                <div className="overline text-muted-foreground mb-2">Link Expiration</div>
                <Select value={linkMode} onValueChange={setLinkMode}>
                  <SelectTrigger className={inputCls}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="offer">Expire with offer deadline</SelectItem>
                    <SelectItem value="custom">Custom date</SelectItem>
                  </SelectContent>
                </Select>
                {linkMode === "custom" && (
                  <div className="mt-2"><DateTimePicker value={linkCustom} onChange={setLinkCustom} /></div>
                )}
              </div>
              <div className="rounded-xl border border-primary/25 bg-primary/[0.06] p-4 text-sm text-muted-foreground">
                On save, a cryptographically-random secure link is generated. You'll get a share card with the link + PIN and copy buttons on the next screen.
              </div>
            </div>
          )}
        </motion.div>
      </AnimatePresence>

      {/* Footer nav */}
      <div className="flex items-center justify-between mt-8">
        <Button variant="ghost" onClick={() => (step === 0 ? nav(-1) : setStep((s) => s - 1))} data-testid="builder-prev">
          <ArrowLeft size={16} className="mr-1" /> {step === 0 ? "Cancel" : "Back"}
        </Button>
        {step < 3 ? (
          <Button onClick={proceed} data-testid="builder-next" disabled={!loaded} className="font-semibold">
            {!loaded ? "Loading…" : <>Continue <ArrowRight size={16} className="ml-1" /></>}
          </Button>
        ) : (
          <Button onClick={proceed} disabled={saving || !loaded} data-testid="builder-save" className="font-semibold">
            {saving ? "Saving…" : editing ? "Save Changes" : "Create Deal & Generate Link"}
          </Button>
        )}
      </div>
    </div>
  );
}

function PreviewRow({ label, value, big, accent }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className={`num font-semibold ${big ? "text-2xl" : "text-sm"} ${accent ? "text-primary" : ""}`}>{value}</span>
    </div>
  );
}
